import { useContext, useEffect } from 'react';

import useIsManagementSection from 'hooks/useIsManagementSection';
import useIsOutsideTeamScope from 'hooks/useIsOutsideTeamScope';
import useSentinelTeam from 'hooks/useSentinelTeam';

import { CurrentInvitationContext } from 'utils/Invitation';

const defaultFaviconLink = document.querySelector('link[rel="icon"]');
const defaultFaviconId = defaultFaviconLink?.id;
const defaultFaviconHref = defaultFaviconLink?.href;

const dynamicFaviconId = 'dynamic-favicon';

export const FLEET_MANAGEMENT_FAVICON_URL = '/FleetManagementFavicon.svg';

/**
 * Unsets the favicon by removing the link element with the provided ID from the head.
 *
 * NOTE: This must be extracted from `useEffect` to work as expected.
 */
function unsetFavicon(id) {
  const links = document.querySelectorAll(`#${id}`);
  if (links.length) {
    links.forEach((link) => {
      document.head.removeChild(link);
    });
  }
}

/**
 * Sets a new favicon by creating a new link[rel="icon"] element in the head with the
 * provided id & url.
 *
 * NOTE: This must be extracted from `useEffect` to work as expected.
 */
function setFavicon(id, url) {
  const link = document.createElement('link');
  link.id = id;
  link.rel = 'icon';
  link.href = url;
  document.head.appendChild(link);
}

function FaviconManager() {
  const { sentinelTeam: invitationSentinelTeam } = useContext(
    CurrentInvitationContext,
  );
  const userSentinelTeam = useSentinelTeam();

  const isManagementSection = useIsManagementSection();
  const isOutsideTeamScope = useIsOutsideTeamScope();

  const sentinelTeam = invitationSentinelTeam || userSentinelTeam;
  // Fleet Management spans teams, so it never shows the last team's icon. The
  // last-viewed team also stays in context on the other All Teams pages, so
  // they get the default icon, same as BrandInfo's Decisiv branding.
  let faviconUrl = sentinelTeam?.favicon?.url;
  if (isManagementSection) faviconUrl = FLEET_MANAGEMENT_FAVICON_URL;
  else if (isOutsideTeamScope) faviconUrl = undefined;

  useEffect(() => {
    if (faviconUrl) {
      // deactivate/ delete default icon
      unsetFavicon(defaultFaviconId);

      // Swap in a fresh link before dropping the old one: browsers don't
      // reliably repaint the tab icon for an in-place href change or a
      // remove-then-add in the same tick (e.g. leaving Fleet Management).
      const oldLink = document.getElementById(dynamicFaviconId);
      if (oldLink?.getAttribute('href') !== faviconUrl) {
        oldLink?.removeAttribute('id');
        setFavicon(dynamicFaviconId, faviconUrl);
        oldLink?.remove();
      }
    } else {
      // Same order as the swap above: add the default icon before dropping
      // the dynamic one, so the tab repaints (e.g. Fleet Management -> /oem_onboard).
      if (!document.getElementById(defaultFaviconId)) {
        setFavicon(defaultFaviconId, defaultFaviconHref);
      }
      unsetFavicon(dynamicFaviconId);
    }
  }, [faviconUrl]);

  return null;
}

export default FaviconManager;
